import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl, firebaseFunctionsBaseUrl } from 'constants/apiBaseUrl'
import { getHeaders } from '@/core/getHeaders'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { OLSAdminAction, OLSAdminActionResponse } from '@otw/models/ols'

interface AdminActionParams {
  action: OLSAdminAction
  categoryId?: string
  versionId?: string
}

/**
 * Hook for OLS admin actions (retrain, promote, rollback, archive).
 *
 * Routing:
 * - retrain → Firebase Function (Cloud Function for performance)
 * - promote / rollback / archive → Express API (for simple operations)
 */
export const useOLSActions = () => {
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useSnackbar()

  // Training goes to Firebase Function (better performance for compute-intensive task)
  const trainAction = useMutation<OLSAdminActionResponse, Error, void>({
    mutationFn: async () => {
      const headers = await getHeaders()
      const response = await fetch(`${firebaseFunctionsBaseUrl}/callable-olsAdminAction`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'retrain' }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Training failed')
      }

      return response.json()
    },
    onSuccess: (result) => {
      if (result.success) {
        showSuccess(result.message)
        queryClient.invalidateQueries({ queryKey: ['ols_categories'] })
        queryClient.invalidateQueries({ queryKey: ['ols_versions', 'weighted_least_squares'] })
        queryClient.invalidateQueries({ queryKey: ['ols_training_logs'] })
      } else {
        showError(result.message)
      }
    },
    onError: (error) => {
      showError(error.message)
    },
  })

  // Other admin actions go to Express API (promote, rollback, archive)
  const adminAction = useMutation<OLSAdminActionResponse, Error, AdminActionParams>({
    mutationFn: async (params) => {
      const headers = await getHeaders()
      const response = await fetch(`${apiBaseUrl}/portal/pricing/ols/admin-action`, {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Action failed')
      }

      return response.json()
    },
    onSuccess: (result, variables) => {
      if (result.success) {
        showSuccess(result.message)
        queryClient.invalidateQueries({ queryKey: ['ols_categories'] })
        if (variables.categoryId) {
          queryClient.invalidateQueries({ queryKey: ['ols_versions', variables.categoryId] })
        }
        queryClient.invalidateQueries({ queryKey: ['ols_training_logs'] })
      } else {
        showError(result.message)
      }
    },
    onError: (error) => {
      showError(error.message)
    },
  })

  const trainWLSModel = () => trainAction.mutateAsync()

  const promoteVersion = (categoryId: string, versionId: string) =>
    adminAction.mutateAsync({ action: 'promote', categoryId, versionId })

  const rollbackVersion = (categoryId: string, versionId: string) =>
    adminAction.mutateAsync({ action: 'rollback', categoryId, versionId })

  const archiveVersion = (categoryId: string, versionId: string) =>
    adminAction.mutateAsync({ action: 'archive', categoryId, versionId })

  return {
    trainAction,
    adminAction,
    trainWLSModel,
    promoteVersion,
    rollbackVersion,
    archiveVersion,
    isTraining: trainAction.isPending,
    isLoading: adminAction.isPending,
  }
}

/**
 * Hook for toggling auto-promote for a category.
 */
export const useOLSToggleAutoPromote = () => {
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useSnackbar()

  return useMutation({
    mutationFn: async ({ categoryId, enabled }: { categoryId: string; enabled: boolean }) => {
      const headers = await getHeaders()
      const response = await fetch(`${apiBaseUrl}/portal/pricing/ols/toggle-auto-promote`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ categoryId, enabled }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to toggle auto-promote')
      }

      return response.json()
    },
    onSuccess: (result, variables) => {
      if (result.success) {
        showSuccess(result.message)
        queryClient.invalidateQueries({ queryKey: ['ols_categories'] })
      } else {
        showError(result.message)
      }
    },
    onError: (error: Error) => {
      showError(error.message)
    },
  })
}
